import { View, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import React from 'react';

import { getFullMachineName } from '../../../../common/utils/machine';
import { ROLE_PERMISSIONS, POS_TYPE, COMMUNICATION_TYPE_NAME } from '../../../../common/constants';
import getPOSModelName from '../../../../common/utils/machine/getPOSModelName';
import { useUserContext } from '../../../../common/utils/context';
import { valueExists } from '../../../../common/utils/utils';

import { Icon, Separator, TypographyParagraph } from '../../../components';
import { ErrorCallout } from '../../../components/callout';

import MachineCommunication from '../../machine/MachineCommunication';
import MachineTemperature from '../../machine/MachineTemperature';
import { useTheme } from '../../../theme';

const DocketPOSDetail = ({ docket, styles }) => {
	const navigation = useNavigation();
	const { hasPermission } = useUserContext();
	const { theme } = useTheme();
	const iconColor = theme.text2;

	const canSeeMachineDetail = hasPermission(ROLE_PERMISSIONS.VIEW_MACHINE_DETAIL_PAGE);
	const canCreateNotes = hasPermission(ROLE_PERMISSIONS.ADD_EDIT_NOTES);

	const { machine } = docket;
	const isMarket = machine.type === POS_TYPE.MARKET;

	const isOffline = !machine.commDevices?.length;

	const machineName = getFullMachineName(machine, machine.place, machine.location);

	const goToMachineTabsView = () => {
		navigation.push('MachineDetailTabView', {
			machineId: machine.id,
			machineName,
			locationId: machine.location?.id,
			isMarket,
		});
	};

	const goToMachineNotes = () => {
		navigation.push('MachineNotes', {
			machineId: machine.id,
		});
	};

	const goToPhotoHistory = () => {
		navigation.push('MachinePhotoHistoryView', {
			machineId: machine.id,
			machineName,
			excludeDocketId: docket.id,
		});
	};

	const unimportantMachineNotes = machine.noteCount - (machine.importantNotes ? machine.importantNotes.length : 0);

	const posModelName = getPOSModelName(machine);

	// Null entries have no meaning; unknown values (newer server enums) fall back to their raw
	// key so an older app build never falsely claims the machine receives no communication.
	const receivedCommunicationTypeNames = (machine.receivedCommunicationTypes || [])
		.filter(Boolean)
		.map(type => COMMUNICATION_TYPE_NAME[type] || type);

	return (
		<View>
			<Separator style={styles.separator} />
			<Text style={styles.headerText}>{`${posModelName} details`}</Text>

			<View style={styles.detailView}>
				<View style={{ flexDirection: 'column' }}>
					<View style={{ flexDirection: 'row', marginRight: 8, marginBottom: 4 }}>
						<View style={styles.icon}>
							<Icon name="signal" size={12} color={iconColor} style={{ marginTop: 2 }} />
						</View>
						<View style={{ flex: 1 }}>
							<View style={{ flexDirection: 'row' }}>
								<TypographyParagraph
									style={{ fontSize: 14 }}
									text={isOffline
										? `Offline ${posModelName.toLowerCase()}`
										: `${isMarket ? 'Kiosk(s)' : 'Communication device'}: ${machine.commDevices.map(commDevice => commDevice.deviceId).join(', ')}`
									}
								/>
							</View>
							{
								!isOffline && (
									<>
										<View style={{ flexDirection: 'row', marginTop: 4 }}>
											<TypographyParagraph
												style={{ fontSize: 14 }}
												text={receivedCommunicationTypeNames.length
													? `Received communication type(s): ${receivedCommunicationTypeNames.join(' | ')}`
													: `${posModelName} doesn't receive communication`
												}
											/>
										</View>
										<View style={{ flexDirection: 'row', marginTop: 4 }}>
											<TypographyParagraph style={{ marginRight: 2, fontSize: 14 }} text="Last communication time:" />
											<MachineCommunication
												communication={machine.lastCommunication}
											/>
										</View>
										{
											valueExists(machine.temperatureCelcius) && (
												<View style={{ flexDirection: 'row', marginTop: 4 }}>
													<TypographyParagraph style={{ marginRight: 2, fontSize: 14 }} text="Last reported temperature:" />
													<MachineTemperature
														temperature={machine.temperatureCelcius}
													/>
												</View>
											)
										}
									</>
								)
							}
							{
								machine.wrap?.name && (
									<View style={{ flexDirection: 'row', marginTop: 4 }}>
										<TypographyParagraph
											style={{ marginRight: 2, fontSize: 14 }}
											text={`Wrap: ${machine.wrap.name}`}
										/>
									</View>
								)
							}
						</View>
					</View>
				</View>
				{
					// The notes link is the only way into the notes screen, so note-adders keep
					// the row even with zero notes; other users skip the noise-only zero row.
					(Boolean(machine.noteCount) || canCreateNotes) && (
						<View style={{ flexDirection: 'row', marginRight: 8, marginBottom: 4 }}>
							<View style={styles.icon}>
								<Icon name="note" size={12} color={iconColor} style={{ marginTop: 3 }} />
							</View>
							<View style={{ flex: 1 }}>
								{
									Boolean(machine.importantNotes) && machine.importantNotes.map(importantNote => (
										<ErrorCallout
											key={`m_note_${importantNote.id}`}
											text={importantNote.description}
										/>
									))
								}
								{
									(unimportantMachineNotes > 0 || canCreateNotes) && (
										<TypographyParagraph
											style={{ fontSize: 16 }}
											text={unimportantMachineNotes > 0
												? `${unimportantMachineNotes} ${Boolean(machine.importantNotes) && machine.importantNotes.length ? 'other ' : ''}${posModelName.toLowerCase()} ${unimportantMachineNotes === 1 ? 'note' : 'notes'}`
												: `Add ${posModelName.toLowerCase()} note`
											}
											href={true}
											onPress={goToMachineNotes}
										/>
									)
								}
							</View>
						</View>
					)
				}
				<View style={{ flexDirection: 'row', marginRight: 8, marginBottom: 4 }}>
					<View style={styles.icon}>
						<Icon name="camera" size={12} color={iconColor} style={{ marginTop: 3 }} />
					</View>
					<View style={{ flex: 1 }}>
						{/* Link points drivers to a grouped gallery of earlier service photos */}
						<TypographyParagraph
							style={{ fontSize: 16 }}
							text="View previous photos"
							href={true}
							onPress={goToPhotoHistory}
						/>
					</View>
				</View>
				{
					Boolean(canSeeMachineDetail) && (
						<View style={{ flexDirection: 'row', marginRight: 8 }}>
							<View style={styles.icon}>
								<Icon name="moreHorizontal" size={12} color={iconColor} style={{ marginTop: 3 }} />
							</View>
							<View style={{ flex: 1 }}>
								<TypographyParagraph style={{ fontSize: 16 }} text={`View all ${posModelName.toLowerCase()} details`} href={true} onPress={goToMachineTabsView} />
							</View>
						</View>
					)
				}
			</View>
		</View>
	);
};

export default DocketPOSDetail;
